/**
 * Per-biome and per-theme color palettes. Colors are stored as 32-bit ARGB
 * ints (matching the engine) and unpacked on demand for Canvas drawing.
 *
 * ## Color rules (so safe-vs-danger is unambiguous on any background)
 *
 *  - **Walls/obstacles** use a dark `wallFill` (the body) and a bright
 *    green-cyan `wallEdge` (the gap markers). The gap markers are the
 *    player's "this is the safe hole" signal.
 *  - **Food** is bright yellow. Always safe to collect.
 *  - **Bonuses** use a hot magenta halo, distinct from food.
 *  - **Danger** (bees, coral, comets) is bright red/orange — clearly hostile.
 *  - **Shield pickup** is sky blue — clearly defensive.
 *
 * Picked to maintain contrast across all four biome backgrounds.
 */
import { Biome } from '../game/engine/types';
import type { ColorBlindMode, GlowLevel, ThemeChoice } from '../data/settings';

export interface Palette {
  bgTop: number;
  bgBottom: number;
  snakeBody: number;
  snakeHead: number;
  snakeStripe: number;
  food: number;
  wallFill: number;
  wallEdge: number;
  danger: number;
  accent: number;
  shield: number;
  bonus: number;
  text: number;
  hudBg: number;
  hudFg: number;
  golden: number;
}

function argb(a: number, r: number, g: number, b: number): number {
  return (((a & 0xff) << 24) | ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff)) >>> 0;
}

function biomeBase(b: Biome): {
  bgTop: number;
  bgBottom: number;
  accent: number;
  danger: number;
} {
  switch (b) {
    case Biome.MEADOW:
      return {
        bgTop: argb(0xff, 0xb6, 0xf0, 0x88),
        bgBottom: argb(0xff, 0x6d, 0xb0, 0x53),
        accent: argb(0xff, 0x90, 0xe8, 0x70),
        danger: argb(0xff, 0xff, 0x4a, 0x30),
      };
    case Biome.REEF:
      return {
        bgTop: argb(0xff, 0x4a, 0x88, 0xb0),
        bgBottom: argb(0xff, 0x1d, 0x3a, 0x55),
        accent: argb(0xff, 0x70, 0xd0, 0xff),
        danger: argb(0xff, 0xff, 0x90, 0x30),
      };
    case Biome.LATTICE:
      return {
        bgTop: argb(0xff, 0x3a, 0x1f, 0x60),
        bgBottom: argb(0xff, 0x10, 0x0a, 0x28),
        accent: argb(0xff, 0xc8, 0x80, 0xff),
        danger: argb(0xff, 0xff, 0x35, 0x80),
      };
    case Biome.MAELSTROM:
      return {
        bgTop: argb(0xff, 0xa0, 0x40, 0x20),
        bgBottom: argb(0xff, 0x30, 0x10, 0x05),
        accent: argb(0xff, 0xff, 0xa0, 0x40),
        // Yellow-ish danger so it pops against the red Maelstrom bg.
        danger: argb(0xff, 0xff, 0xc0, 0x40),
      };
  }
}

function cbShift(color: number, cb: ColorBlindMode): number {
  if (cb === 'OFF') return color;
  const a = (color >>> 24) & 0xff;
  const r = (color >>> 16) & 0xff;
  const g = (color >>> 8) & 0xff;
  const b = color & 0xff;
  if (cb === 'BLUE_YELLOW') return argb(a, b, g, r);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max - min < 40) return argb(a, 255, 255, 255);
  return argb(a, r > 127 ? 255 : 0, g > 127 ? 255 : 0, b > 127 ? 255 : 0);
}

/**
 * Color-blind palette tweaks for the player-meaningful colours
 * (danger / food / bonus / shield). Under HIGH_CONTRAST we collapse
 * them to four maximally-saturated primaries so deuteranopes and
 * protanopes still see the hue split. Under BLUE_YELLOW we swap R/B
 * which flips warm/cool — the dominant cue these readers rely on.
 */
function cbShiftSafe(
  color: number,
  kind: 'danger' | 'food' | 'bonus' | 'shield',
  cb: ColorBlindMode,
): number {
  if (cb === 'OFF') return color;
  const a = (color >>> 24) & 0xff;
  const r = (color >>> 16) & 0xff;
  const g = (color >>> 8) & 0xff;
  const b = color & 0xff;
  if (cb === 'HIGH_CONTRAST') {
    if (kind === 'danger') return argb(a, 0xff, 0x20, 0x20);
    if (kind === 'food') return argb(a, 0xff, 0xff, 0x00);
    if (kind === 'bonus') return argb(a, 0xff, 0x00, 0xff);
    return argb(a, 0x00, 0xff, 0xff); // shield
  }
  return argb(a, b, g, r);
}

export function paletteFor(
  biome: Biome,
  _theme: ThemeChoice,
  cb: ColorBlindMode,
  _glow: GlowLevel,
): Palette {
  const base = biomeBase(biome);
  return {
    bgTop: cbShift(base.bgTop, cb),
    bgBottom: cbShift(base.bgBottom, cb),
    snakeBody: cbShift(argb(0xff, 0xff, 0xff, 0xff), cb),
    snakeHead: cbShift(argb(0xff, 0xff, 0xe0, 0x60), cb),
    snakeStripe: cbShift(argb(0xff, 0xff, 0xa0, 0x40), cb),
    food: cbShiftSafe(argb(0xff, 0xff, 0xe6, 0x40), 'food', cb),
    wallFill: cbShift(argb(0xff, 0x28, 0x36, 0x58), cb),
    wallEdge: cbShift(argb(0xff, 0x40, 0xff, 0xc0), cb),
    danger: cbShiftSafe(base.danger, 'danger', cb),
    accent: cbShift(base.accent, cb),
    shield: cbShiftSafe(argb(0xff, 0x40, 0xb0, 0xff), 'shield', cb),
    bonus: cbShiftSafe(argb(0xff, 0xff, 0x60, 0xd0), 'bonus', cb),
    text: argb(0xff, 0xff, 0xff, 0xff),
    hudBg: argb(0xcc, 0x10, 0x10, 0x18),
    hudFg: argb(0xff, 0xff, 0xff, 0xff),
    golden: cbShiftSafe(argb(0xff, 0xff, 0xd0, 0x00), 'bonus', cb),
  };
}

/** Apply alpha (0..1) to an ARGB color. */
export function withAlpha(color: number, alpha: number): number {
  const a = Math.round(alpha * 255) & 0xff;
  return ((color & 0x00ffffff) | (a << 24)) >>> 0;
}

/**
 * Map a GlowLevel to a Canvas shadow blur radius (in screen pixels).
 * OFF returns 0 so the per-call shadow work is skipped.
 */
export function glowBlurRadius(glow: GlowLevel, scale: number): number {
  switch (glow) {
    case 'OFF':
      return 0;
    case 'LOW':
      return 8 * scale;
    case 'MEDIUM':
      return 16 * scale;
    case 'HIGH':
      return 28 * scale;
    default:
      return 0;
  }
}
